import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChevronUp, ChevronDown, Link as LinkIcon, ExternalLink } from "lucide-react";
import { STORE_OPTIONS, STORE_BY_VALUE, DEFAULT_STORE } from "@/lib/storeOptions";

/**
 * Per-game Links editor (admin-only — embedded in MDB Edit Game dialog).
 *
 * Props:
 *  - value: current array of link objects (or undefined)
 *  - onChange: (newArray) => void
 *
 * Each link is { id, storeName, heading, url, thumbnailUrl, description, sortOrder }.
 * sortOrder is rewritten on every change to match array order.
 *
 * Reorder uses up/down arrows for accessibility — no external DnD library
 * required so the dialog stays lightweight.
 */
export default function GameLinksEditor({ value, onChange }) {
  const links = Array.isArray(value) ? value : [];
  const [open, setOpen] = useState(null); // index of expanded row (for adding/editing)

  const update = (next) => {
    // Always keep sortOrder in sync with array index.
    const normalized = next.map((l, i) => ({ ...l, sortOrder: i }));
    onChange(normalized);
  };

  const addLink = () => {
    const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `lnk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const next = [
      ...links,
      { id, storeName: DEFAULT_STORE, heading: "", url: "", thumbnailUrl: "", description: "", sortOrder: links.length },
    ];
    update(next);
    setOpen(next.length - 1);
  };

  const deleteLink = (idx) => {
    update(links.filter((_, i) => i !== idx));
    setOpen(null);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= links.length) return;
    const next = [...links];
    [next[idx], next[j]] = [next[j], next[idx]];
    update(next);
  };

  const patch = (idx, field, val) => {
    const next = [...links];
    next[idx] = { ...next[idx], [field]: val };
    update(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <LinkIcon className="w-4 h-4 text-cyan-400" />
          Store / External Links
        </Label>
        <Button type="button" size="sm" variant="outline" onClick={addLink} data-testid="add-game-link-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Link
        </Button>
      </div>
      <p className="text-xs text-white/40">
        Shown to users on the Browse page below the Amazon affiliate link. Drag-to-reorder via the ↑/↓ buttons.
      </p>

      {links.length === 0 ? (
        <div className="text-xs text-white/30 italic px-2 py-3 bg-black/20 rounded border border-white/5">
          No links yet. Click &quot;Add Link&quot; to attach a store or reference URL.
        </div>
      ) : (
        <div className="space-y-1.5">
          {links.map((link, idx) => {
            const store = STORE_BY_VALUE[link.storeName] || STORE_BY_VALUE.other;
            const isOpen = open === idx;
            return (
              <div
                key={link.id || idx}
                className="rounded-md border border-white/10 bg-white/[0.02]"
                data-testid={`game-link-row-${idx}`}
              >
                <div className="flex items-center gap-2 p-2">
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide flex-shrink-0"
                    style={{ backgroundColor: store.color + "30", color: store.color, border: `1px solid ${store.color}40` }}
                  >
                    {store.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : idx)}
                    className="flex-1 text-left text-sm text-white truncate hover:text-cyan-300"
                    data-testid={`game-link-toggle-${idx}`}
                  >
                    {link.heading || <span className="text-white/40 italic">Untitled link</span>}
                  </button>
                  {link.url && (
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/40 hover:text-white/80"
                      title="Open link"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" title="Move up" data-testid={`game-link-up-${idx}`}>
                    <ChevronUp className="w-3.5 h-3.5 text-white/50" />
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === links.length - 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" title="Move down" data-testid={`game-link-down-${idx}`}>
                    <ChevronDown className="w-3.5 h-3.5 text-white/50" />
                  </button>
                  <button type="button" onClick={() => deleteLink(idx)} className="p-1 rounded hover:bg-red-500/20" title="Delete" data-testid={`game-link-delete-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                  </button>
                </div>
                {isOpen && (
                  <div className="p-3 border-t border-white/10 space-y-2 bg-black/20">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-white/60">Store</Label>
                        <Select value={link.storeName} onValueChange={(v) => patch(idx, "storeName", v)}>
                          <SelectTrigger className="bg-zinc-900 border-white/10 h-8 text-sm" data-testid={`game-link-store-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-72">
                            {STORE_OPTIONS.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-white/60">Heading</Label>
                        <Input
                          value={link.heading || ""}
                          onChange={(e) => patch(idx, "heading", e.target.value)}
                          placeholder="e.g., Buy on Steam"
                          className="bg-zinc-900 border-white/10 h-8 text-sm"
                          data-testid={`game-link-heading-${idx}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">URL</Label>
                      <Input
                        value={link.url || ""}
                        onChange={(e) => patch(idx, "url", e.target.value)}
                        placeholder="https://..."
                        className="bg-zinc-900 border-white/10 h-8 text-sm"
                        data-testid={`game-link-url-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Thumbnail URL (optional)</Label>
                      <Input
                        value={link.thumbnailUrl || ""}
                        onChange={(e) => patch(idx, "thumbnailUrl", e.target.value)}
                        placeholder="https://..."
                        className="bg-zinc-900 border-white/10 h-8 text-sm"
                        data-testid={`game-link-thumb-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Description (optional)</Label>
                      <Textarea
                        value={link.description || ""}
                        onChange={(e) => patch(idx, "description", e.target.value)}
                        placeholder="Short note shown next to the link"
                        className="bg-zinc-900 border-white/10 text-sm min-h-[60px]"
                        data-testid={`game-link-desc-${idx}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
